<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

import { useColumnResize } from '../../composables/useColumnResize'
import {
  cellKey,
  createLayoutDrag,
  injectLayoutDrag,
  provideLayoutDrag,
  useGridRowDrop,
} from '../../composables/useLayoutDrag'
import type { DashboardGridAncestor, DashboardWidgetTarget } from '../../models/builder.types'
import type { DashboardCell, DashboardRow } from '../../models/layout.types'
import type { WidgetDataSource, WidgetSheet } from '../../models/widgetExport.types'
import {
  addTab,
  appendRow,
  duplicateRow,
  duplicateTab,
  removeCell,
  removeRow,
  removeTab,
  renameRow,
  renameTab,
  resizeRowCells,
  setTabRows,
  type RowLayout,
} from '../../service/layout/editLayout'

import AddBlockCard from './AddBlockCard.vue'
import GridCell from './GridCell.vue'
import GridRowHeader from './GridRowHeader.vue'

import { useI18n } from '@/shared/i18n'
import { OverlayPortal } from '@/shared/ui'

defineOptions({ name: 'DashboardGrid' })

const props = withDefaults(
  defineProps<{
    rows: readonly DashboardRow[]
    editMode?: boolean
    readOnly?: boolean
    wsId?: number | string | null
    accountWorkspaces?: readonly (number | string)[]
    path?: readonly DashboardGridAncestor[]
    /** Sheet-name prefix for the export fan-out; only a nested tab grid carries one (§8). */
    tabName?: string
    /** Cells in the row that owns this tab grid; v3 offered split layouts only under a 1-col row. */
    parentCellCount?: number
  }>(),
  {
    editMode: false,
    readOnly: false,
    wsId: null,
    accountWorkspaces: () => [],
    path: () => [],
    tabName: '',
    parentCellCount: 0,
  },
)
const emit = defineEmits<{
  'update:rows': [rows: DashboardRow[]]
  'request-widget': [target: DashboardWidgetTarget]
}>()
const { t } = useI18n()
const resize = useColumnResize()
const editable = computed(() => props.editMode && !props.readOnly)
const nested = computed(() => props.path.length > 0)

/**
 * The root grid owns the drag session unless a parent (the surface, which also renders the outline
 * panel) already opened one; every nested grid joins whichever session is above it.
 */
const drag =
  injectLayoutDrag() ??
  provideLayoutDrag(
    createLayoutDrag({
      rows: () => props.rows,
      commit: (rows) => emit('update:rows', rows),
    }),
  )
const { startRow, isDraggedRow, showsLineAt, lineEdges, lineClass, onRowsOver, onEndOver } =
  useGridRowDrop(drag, props)

/**
 * A cell's `${cell.width}%` flex-basis is an inline style, which always outranks a plain utility
 * class — so forcing it back to `auto` below the `sm` breakpoint (`max-sm:flex-col` turns the row
 * into a column, where `flex-basis` sets height, not width) used to need `max-sm:!basis-auto`.
 * Tracking the breakpoint here instead lets the binding itself drop the inline style on mobile,
 * so no `!important` is needed to win against it (mirrors `AppTopbar.vue`'s nav breakpoint).
 */
const isMobileStack = ref(false)
let mobileStackMedia: MediaQueryList | null = null

function syncMobileStack(event: MediaQueryList | MediaQueryListEvent): void {
  isMobileStack.value = event.matches
}

onMounted(() => {
  mobileStackMedia = window.matchMedia('(max-width: 40rem)')
  syncMobileStack(mobileStackMedia)
  mobileStackMedia.addEventListener('change', syncMobileStack)
})

onUnmounted(() => mobileStackMedia?.removeEventListener('change', syncMobileStack))

/**
 * §8 — the export fan-out. v3 collected renderers into a 3-D `componentRefs` array indexed by
 * section/column/tab (`CustomDashboardSection.vue:604-626`) and walked it with a hand-rolled
 * triple loop that had to know its own depth. Here each grid level owns only its DIRECT children
 * and delegates the rest to the nested grids, so recursion replaces the index arithmetic.
 *
 * `allSettled`, like v3: one widget whose read fails must not take the whole workbook with it.
 */
const sources = new Map<string, WidgetDataSource>()

function collect(key: string, instance: unknown): void {
  if (instance) sources.set(key, instance as WidgetDataSource)
  else sources.delete(key)
}

async function downloadData(): Promise<WidgetSheet[]> {
  const settled = await Promise.allSettled(
    [...sources.values()].map((source) => source.downloadData()),
  )
  return settled.flatMap((result) => (result.status === 'fulfilled' ? result.value : []))
}

defineExpose({ downloadData })

function tabKey(row: DashboardRow, cell: DashboardCell): string {
  return cellKey(props.path, row.id, cell.id)
}

/**
 * v3 `CustomDashboardSection.vue:195-205`: the create card exists to depth 2, and offers the split
 * shapes only at the root and directly under a single-column root section.
 */
const addLayouts = computed<readonly RowLayout[] | null>(() => {
  const depth = props.path.length
  if (depth > 2) return null
  return depth === 0 || (depth === 1 && props.parentCellCount < 2)
    ? ['col', 'classic', 'grid']
    : ['col']
})

/**
 * A root section is born a tab group with one tab per cell. A nested full-width section is born
 * to hold one widget, so the builder opens on it straight away — v3's `addSection` →
 * `modalActive` for `props.index`.
 */
function addSection(layout: RowLayout): void {
  const id = `section-${Date.now()}`
  const options = nested.value ? {} : { tabName: t('dashboards.grid.defaultTab') }
  emit('update:rows', appendRow(props.rows, layout, id, options))
  if (nested.value && layout === 'col') {
    emit('request-widget', {
      ancestors: [...props.path],
      rowId: id,
      cellId: `${id}-col-0`,
      widget: null,
    })
  }
}

function newTabId(): string {
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function onAddTab(row: DashboardRow, cell: DashboardCell): void {
  const id = newTabId()
  drag.activeTabs[tabKey(row, cell)] = id
  emit(
    'update:rows',
    addTab(props.rows, row.id, cell.id, {
      id,
      name: t('dashboards.grid.tabDefaultName', { index: cell.tabs.length + 1 }),
    }),
  )
}

function onDuplicateTab(row: DashboardRow, cell: DashboardCell, tabId: string): void {
  const original = cell.tabs.find((tab) => tab.id === tabId)
  if (!original) return
  const id = newTabId()
  drag.activeTabs[tabKey(row, cell)] = id
  const name = t('dashboards.grid.tabCopyName', {
    name: original.name || t('dashboards.grid.untitledTab'),
  })
  emit('update:rows', duplicateTab(props.rows, row.id, cell.id, tabId, { id, name }, newTabId))
}

/**
 * No bookkeeping on `activeTabs` here on purpose: `resolveActiveTab` already discards an id that
 * no longer names a tab, and that check also covers rows replaced from outside this component (a
 * reload, an undo, a sibling grid's edit). Clearing the entry here too would be a second mechanism
 * for the same rule — and the one that silently stops being exercised.
 */
function onRemoveTab(row: DashboardRow, cell: DashboardCell, tabId: string): void {
  if (cell.tabs.length < 2) return
  emit('update:rows', removeTab(props.rows, row.id, cell.id, tabId))
}

function requestWidget(row: DashboardRow, cell: DashboardCell): void {
  emit('request-widget', {
    ancestors: [...props.path],
    rowId: row.id,
    cellId: cell.id,
    widget: cell.widget,
  })
}

/** Only the root sees what nobody claimed: the pointer left every target, so drop the line. */
function onGridOver(): void {
  if (!nested.value && drag.item.value) drag.clear()
}

function startResize(event: PointerEvent, row: DashboardRow, leftIndex: number): void {
  const left = row.cells[leftIndex]
  const right = row.cells[leftIndex + 1]
  const container = (event.currentTarget as HTMLElement).closest('.dashboard-grid__cells')
  if (!left || !right || !container) return
  resize.start(event, {
    leftWidth: left.width,
    rightWidth: right.width,
    containerWidth: container.getBoundingClientRect().width,
    update: (leftWidth, rightWidth) =>
      emit(
        'update:rows',
        resizeRowCells(props.rows, row.id, left.id, right.id, leftWidth, rightWidth),
      ),
  })
}
</script>

<template>
  <div
    class="dashboard-grid flex flex-col gap-lg"
    :data-depth="path.length"
    data-testid="dashboard-grid"
    @dragover="onGridOver"
  >
    <OverlayPortal>
      <div
        v-if="resize.resizing.value"
        class="fixed inset-0 z-toast cursor-col-resize"
        aria-hidden="true"
      />
    </OverlayPortal>
    <div
      v-if="rows.length"
      class="dashboard-grid__rows flex flex-col gap-lg"
      @dragover="onRowsOver"
      @drop="drag.drop($event)"
    >
      <div v-for="row in rows" :key="row.id" class="relative" data-row-slot>
        <span
          v-for="edge in lineEdges(rows.indexOf(row))"
          :key="edge"
          class="dashboard-grid__drop-line pointer-events-none absolute inset-x-0 z-raised h-0.5 rounded-full bg-accent"
          :class="lineClass(edge)"
          data-testid="dashboard-row-drop-line"
        />
        <section
          class="dashboard-grid__row transition-opacity"
          :class="{ 'opacity-40': isDraggedRow(row) }"
          :data-row-id="row.id"
        >
          <GridRowHeader
            v-if="editable && nested"
            :row="row"
            @rename="emit('update:rows', renameRow(rows, row.id, $event))"
            @remove="emit('update:rows', removeRow(rows, row.id))"
          />
          <div class="dashboard-grid__cells flex gap-sm max-sm:flex-col">
            <article
              v-for="(cell, cellIndex) in row.cells"
              :key="cell.id"
              class="dashboard-grid__cell relative min-w-0 grow max-sm:w-full"
              :style="isMobileStack ? undefined : { flexBasis: `${cell.width}%` }"
            >
              <GridCell
                :ref="(instance) => collect(`cell:${cell.id}`, instance)"
                :row="row"
                :cell="cell"
                :path="path"
                :edit-mode="editMode"
                :read-only="readOnly"
                :ws-id="wsId"
                :account-workspaces="accountWorkspaces"
                :tab-name="tabName"
                @select-tab="drag.activeTabs[tabKey(row, cell)] = $event"
                @add-tab="onAddTab(row, cell)"
                @rename-tab="
                  emit('update:rows', renameTab(rows, row.id, cell.id, $event.tabId, $event.name))
                "
                @duplicate-tab="onDuplicateTab(row, cell, $event)"
                @remove-tab="onRemoveTab(row, cell, $event)"
                @update-tab-rows="
                  (tabId, next) =>
                    emit('update:rows', setTabRows(rows, row.id, cell.id, tabId, next))
                "
                @request-widget="requestWidget(row, cell)"
                @nested-request-widget="emit('request-widget', $event)"
                @row-drag-start="startRow(row.id, $event)"
                @row-drag-end="drag.end()"
                @duplicate="emit('update:rows', duplicateRow(rows, row.id))"
                @remove-cell="emit('update:rows', removeCell(rows, row.id, cell.id))"
                @remove-row="emit('update:rows', removeRow(rows, row.id))"
              />
              <button
                v-if="editable && cellIndex < row.cells.length - 1"
                type="button"
                class="dashboard-grid__resize group/resize absolute top-0 right-0 z-raised h-full w-3 translate-x-[calc(50%+var(--ui-space-sm)/2)] cursor-col-resize border-0 bg-transparent"
                role="separator"
                aria-orientation="vertical"
                :aria-label="t('dashboards.grid.resizeColumns')"
                data-testid="dashboard-resize-columns"
                @pointerdown.prevent="startResize($event, row, cellIndex)"
              >
                <span
                  class="absolute inset-y-1/4 left-1/2 w-0.5 -translate-x-1/2 rounded-full bg-border-strong transition-colors group-hover/resize:bg-accent"
                />
              </button>
            </article>
          </div>
        </section>
      </div>
    </div>

    <AddBlockCard
      v-if="editable && addLayouts"
      :layouts="addLayouts"
      :compact="nested"
      :first="!nested && !rows.length"
      :dropping="drag.item.value?.kind === 'row'"
      :drop-active="showsLineAt(rows.length)"
      @create="addSection"
      @dragover="onEndOver"
      @drop="drag.drop($event)"
    />

    <div
      v-else-if="!rows.length && !nested"
      class="dashboard-grid__empty grid min-h-36 place-items-center text-sm text-muted"
    >
      <span>{{ t('dashboards.grid.noSections') }}</span>
    </div>
  </div>
</template>
